import clsx from 'clsx';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import {
  WEEKDAY_CN,
  addDays,
  cnDate,
  fmtDateWeek,
  fmtMonthDay,
  weekStartOf,
  weekdayOf,
  type ProjectStatus,
  type WeeklyDTO,
  type WeeklyItem,
  type WeeklySection,
} from '@wd/shared';
import { TopBar } from '../../app/AppShell';
import { ApiError, errorText } from '../../lib/api';
import { timeCN, todayCN } from '../../lib/clock';
import { useHotkeys } from '../../lib/hotkeys';
import { useLayout } from '../../lib/media';
import { useWeekly, useWeeklyActions } from '../../lib/queries';
import { Icon, MoreIcon, StatusIcon } from '../../ui/icons';
import { MenuList, Popover } from '../../ui/menu';
import { AIAvatar, Button, EmptyState, Hint, IconButton, Modal, Quote, Skeleton } from '../../ui/primitives';
import './weekly.css';

// 周报（设计稿 14a）：AI 起草、可直接编辑，每一句都能追溯到原文（悬停查看来源）。

const SECTIONS: { key: WeeklySection; label: string; icon: ProjectStatus }[] = [
  { key: 'done', label: '本周完成', icon: 'done' },
  { key: 'progress', label: '推进中', icon: 'progress' },
  { key: 'blocked', label: '阻塞与处理', icon: 'blocked' },
  { key: 'next', label: '下周重点', icon: 'todo' },
];

const whenCN = (iso: string) => {
  const d = cnDate(new Date(iso));
  return `${d === todayCN() ? '今天' : WEEKDAY_CN[weekdayOf(d)]} ${timeCN(iso)}`;
};

/** 复制为文本：直接贴进微信群或邮件 */
function asText(w: WeeklyDTO): string {
  const lines = [`第 ${w.weekNo} 周周报（${fmtMonthDay(w.weekStart)} – ${fmtMonthDay(w.weekEnd)} · ${w.scopeName}）`];
  for (const s of SECTIONS) {
    const items = w.items.filter((i) => i.section === s.key);
    if (!items.length) continue;
    lines.push('', s.label, ...items.map((i) => `· ${i.text}`));
  }
  return lines.join('\n');
}

export function WeeklyPage() {
  const { week } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const layout = useLayout();
  const mobile = layout === 'mobile';
  const scope = params.get('scope');
  const { data, isLoading, error } = useWeekly(week, scope);
  const actions = useWeeklyActions();
  const [toast, setToast] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [menu, setMenu] = useState(false);
  const moreRef = useRef<HTMLButtonElement>(null);
  const scopeRef = useRef<HTMLButtonElement>(null);
  const [scopeMenu, setScopeMenu] = useState(false);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  const thisWeek = weekStartOf(todayCN());
  const goWeek = (ws: string) => navigate(`/weekly/${ws}${data ? `?scope=${data.scope}` : ''}`);
  const copy = () => {
    if (!data?.items.length) return;
    void navigator.clipboard?.writeText(asText(data)).then(() => setToast('已复制，可以直接粘贴到微信或邮件'));
  };
  const canSend = !!data && data.canEdit && data.status === 'draft' && data.items.length > 0 && !!data.id;

  useHotkeys(
    {
      'mod+Enter': () => canSend && setConfirm(true),
      'mod+c': () => {
        // 有选中文字时照常复制
        if (window.getSelection()?.toString()) return false;
        copy();
      },
      ArrowLeft: () => data && goWeek(addDays(data.weekStart, -7)),
      ArrowRight: () => data && data.weekStart < thisWeek && goWeek(addDays(data.weekStart, 7)),
    },
    !mobile && !editing,
  );

  if (error) {
    const forbidden = error instanceof ApiError && error.status === 403;
    return (
      <>
        {!mobile && <TopBar crumbs={['周报']} />}
        <EmptyState icon={forbidden ? 'lock' : 'wifioff'} title={forbidden ? '周报只对主管开放' : '周报没有加载出来'} desc={errorText(error)}>
          {forbidden && <Button onClick={() => navigate('/radar')}>打开项目雷达</Button>}
        </EmptyState>
      </>
    );
  }
  if (isLoading || !data) {
    return (
      <div style={{ padding: '40px 64px' }} aria-busy="true">
        <Skeleton w={200} h={22} />
        <Skeleton w={320} h={12} style={{ marginTop: 12 }} />
      </div>
    );
  }

  const range = `${fmtMonthDay(data.weekStart)} – ${fmtMonthDay(data.weekEnd)}`;
  const drafting = actions.draft.isPending;
  const draft = () =>
    actions.draft.mutate(
      { week: data.weekStart, scope: data.scope },
      { onError: (e) => setToast(errorText(e)), onSuccess: () => setToast('已起草，核对后可以发送') },
    );

  const head = (
    <>
      <div className="wk-print-head">
        Wattsonic · {data.scopeName} · {range}
      </div>
      <h1 className="t-page-title">第 {data.weekNo} 周 周报</h1>
      {data.id && (
        <div className="wk-meta">
          {data.generatedBy === 'ai' ? <AIAvatar size={12} /> : <Icon name="doc" size={12} />}
          <span>{data.generatedBy === 'ai' ? 'AI 根据本周日报起草' : '根据本周日报整理'}</span>
          {data.canEdit && (
            <>
              <span>·</span>
              <span>可直接编辑</span>
            </>
          )}
          <span>·</span>
          <span>每一句都能追溯到原文</span>
        </div>
      )}
    </>
  );

  let body: ReactNode;
  if (drafting) {
    body = (
      <div className="wk-drafting" aria-busy="true">
        <div className="t-meta">正在根据本周日报起草，大约需要十几秒…</div>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ marginTop: 28 }}>
            <Skeleton w={64} h={10} />
            <Skeleton w="92%" h={12} style={{ marginTop: 14 }} />
            <Skeleton w="76%" h={12} style={{ marginTop: 12 }} />
          </div>
        ))}
      </div>
    );
  } else if (!data.id) {
    body = (
      <EmptyState
        icon="report"
        title={data.draftAt ? `本周周报会在${whenCN(data.draftAt)}起草` : '这一周还没有周报'}
        desc={data.draftAt ? '到时会根据本周日报自动整理，并提醒你核对。也可以现在先起草一版。' : '根据这一周提交的日报整理，大约需要十几秒。'}
      >
        {data.canEdit && (
          <Button variant="primary" onClick={draft}>
            {data.draftAt ? '现在起草' : '起草这周的周报'}
          </Button>
        )}
      </EmptyState>
    );
  } else {
    body = SECTIONS.map((s) => {
      const items = data.items.filter((i) => i.section === s.key);
      if (!items.length && !data.canEdit) return null;
      return (
        <Section key={s.key} w={data} label={s.label} section={s.key}>
          {items.map((it) => (
            <Item key={it.id} w={data} it={it} icon={s.icon} mobile={mobile} editing={editing === it.id} setEditing={setEditing} />
          ))}
          {!items.length && <div className="wk-none">这周没有</div>}
        </Section>
      );
    });
  }

  const footer = data.id && !drafting && (
    <div className={clsx('wk-foot', mobile && 'mobile')}>
      {data.status === 'sent' ? (
        <span className="t-meta wk-sent">
          <StatusIcon status="done" />
          已发送 · {fmtMonthDay(cnDate(new Date(data.sentAt!)))} {timeCN(data.sentAt!)}
          {data.sentByName ? ` · ${data.sentByName}` : ''}
        </span>
      ) : data.canEdit ? (
        <Button variant="primary" mobile={mobile} disabled={!canSend} onClick={() => setConfirm(true)}>
          发送给管理层
        </Button>
      ) : null}
      <Button icon="copy" mobile={mobile} disabled={!data.items.length} onClick={copy}>
        复制为文本
      </Button>
      {!mobile && (
        <Button icon="export" disabled={!data.items.length} onClick={() => window.print()}>
          导出 PDF
        </Button>
      )}
      <span className="flex1" />
      {toast && <span className="t-meta wk-toast">{toast}</span>}
      {!mobile && !toast && (
        <span className="row" style={{ gap: 14 }}>
          {canSend && <Hint keys={['⌘', '↵']}>发送</Hint>}
          <Hint keys={['⌘', 'C']}>复制</Hint>
        </span>
      )}
    </div>
  );

  const confirmModal = confirm && data.id && (
    <Modal
      width={440}
      title="发送给管理层"
      subtitle={`第 ${data.weekNo} 周 · ${data.scopeName}`}
      onClose={() => setConfirm(false)}
      footNote="发送后不能再修改"
      footer={
        <Button
          variant="primary"
          disabled={actions.send.isPending}
          onClick={() =>
            actions.send.mutate(data.id!, {
              onSuccess: (r) => {
                setConfirm(false);
                setToast(`已发送给 ${r.recipients} 人`);
              },
              onError: (e) => setToast(errorText(e)),
            })
          }
        >
          发送
        </Button>
      }
    >
      <div className="t-body">
        {data.items.length} 条，其中阻塞 {data.items.filter((i) => i.section === 'blocked').length} 条。
        {data.recipients.length ? `会通知 ${data.recipients.map((r) => r.name).join('、')}。` : '目前没有其他主管，发送后只保存记录。'}
      </div>
    </Modal>
  );

  if (mobile) {
    return (
      <div className="wk-m">
        <div className="wk-m-bar">
          <IconButton name="chevL" label="上一周" mobile onClick={() => goWeek(addDays(data.weekStart, -7))} />
          <span className="flex1 c2" style={{ textAlign: 'center', fontSize: 15 }}>
            {range}
          </span>
          <IconButton name="chevR" label="下一周" mobile disabled={data.weekStart >= thisWeek} onClick={() => goWeek(addDays(data.weekStart, 7))} />
        </div>
        <div className="wk-m-scroll">
          {head}
          <div className="t-meta" style={{ marginTop: 6 }}>
            {data.scopeName}
            {data.id ? ` · ${data.reportCount} 份日报 · ${data.anomalyCount} 条异常` : ''}
          </div>
          {body}
        </div>
        {footer}
        {confirmModal}
      </div>
    );
  }

  return (
    <>
      <TopBar crumbs={['周报', `第 ${data.weekNo} 周`]}>
        <span className="t-meta" style={{ marginRight: 8 }}>
          {range}
        </span>
        <IconButton name="chevL" label="上一周（←）" onClick={() => goWeek(addDays(data.weekStart, -7))} />
        <IconButton name="chevR" label="下一周（→）" disabled={data.weekStart >= thisWeek} onClick={() => goWeek(addDays(data.weekStart, 7))} />
        <button ref={moreRef} type="button" className="wd-btn icon" aria-label="更多" onClick={() => setMenu(!menu)}>
          <MoreIcon />
        </button>
        <Popover anchor={moreRef} open={menu} onClose={() => setMenu(false)} width={220} align="end">
          <MenuList
            items={[
              ...(data.canEdit && data.id ? [{ value: 'redraft', label: '重新起草（覆盖修改）', icon: <Icon name="refresh" size={14} /> }] : []),
              { value: 'this', label: '回到本周', icon: <Icon name="cal" size={14} /> },
            ]}
            selected={null}
            onPick={(v) => {
              setMenu(false);
              if (v === 'redraft') draft();
              else navigate(`/weekly?scope=${data.scope}`);
            }}
          />
        </Popover>
      </TopBar>
      <div className="wk">
        <div className="wk-main">
          <div className="wk-in">
            {head}
            {body}
          </div>
        </div>
        {layout === 'full' && (
          <aside className="wk-panel">
            <div>
              <div className="wk-kv">
                <span className="k">状态</span>
                <span className="v">
                  {data.status === 'sent' ? <StatusIcon status="done" /> : <StatusIcon status="todo" />}
                  {data.status === 'sent' ? '已发送' : data.id ? '草稿 · 未发送' : '还没起草'}
                </span>
              </div>
              <div className="wk-kv">
                <span className="k">范围</span>
                {data.scopes.length > 1 ? (
                  <button ref={scopeRef} type="button" className="v wk-pick" onClick={() => setScopeMenu(!scopeMenu)}>
                    {data.scopeName}
                    <Icon name="chevD" size={12} />
                  </button>
                ) : (
                  <span className="v">{data.scopeName}</span>
                )}
                <Popover anchor={scopeRef} open={scopeMenu} onClose={() => setScopeMenu(false)} width={180}>
                  <MenuList
                    items={data.scopes.map((s) => ({ value: s.id, label: s.name }))}
                    selected={data.scope}
                    onPick={(v) => {
                      setScopeMenu(false);
                      setParams({ scope: v }, { replace: true });
                    }}
                  />
                </Popover>
              </div>
              {data.id && (
                <div className="wk-kv">
                  <span className="k">来源</span>
                  <span className="v">
                    {data.reportCount} 份日报
                    <span className="t-meta">{data.anomalyCount} 条异常</span>
                  </span>
                </div>
              )}
              {data.generatedAt && (
                <div className="wk-kv">
                  <span className="k">生成</span>
                  <span className="v">
                    {whenCN(data.generatedAt)}
                    {data.editedAt && <span className="t-meta">已修改</span>}
                  </span>
                </div>
              )}
              <div className="wk-kv">
                <span className="k">发送给</span>
                <span className="v" title={data.recipients.map((r) => r.name).join('、')}>
                  管理层
                  <span className="t-meta">{data.recipients.length} 人</span>
                </span>
              </div>
            </div>
            <div className="hr" />
            <div className="wk-note">
              <span className="name">说明</span>
              <span>周报只汇总项目，不点评个人。“建议关注”名单不会出现在周报里。</span>
            </div>
          </aside>
        )}
      </div>
      {footer}
      {confirmModal}
    </>
  );
}

// ---------------------------------------------------------------------------
// 一句周报：点击编辑，悬停看来源
// ---------------------------------------------------------------------------

function Item({
  w,
  it,
  icon,
  mobile,
  editing,
  setEditing,
}: {
  w: WeeklyDTO;
  it: WeeklyItem;
  icon: ProjectStatus;
  mobile: boolean;
  editing: boolean;
  setEditing: (id: string | null) => void;
}) {
  const navigate = useNavigate();
  const { edit, remove } = useWeeklyActions();
  const [text, setText] = useState(it.text);
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  // 下方放不下时，来源浮层改为向上弹出
  const place = () => {
    const row = rowRef.current;
    if (!row) return;
    const r = row.getBoundingClientRect();
    const box = row.closest('.wk-main')?.getBoundingClientRect();
    const below = (box?.bottom ?? window.innerHeight) - r.bottom;
    const above = r.top - (box?.top ?? 0);
    setUp(below < 300 && above > below);
  };
  useEffect(() => setText(it.text), [it.text]);
  useEffect(() => {
    if (!editing || !ref.current) return;
    const el = ref.current;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [editing]);
  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = 'auto';
      ref.current.style.height = `${ref.current.scrollHeight}px`;
    }
  }, [text, editing]);

  const save = () => {
    setEditing(null);
    const t = text.trim();
    if (!t) return setText(it.text);
    if (t !== it.text) edit.mutate({ id: w.id!, itemId: it.id, text: t });
  };

  const shown = mobile ? it.sources : it.sources.slice(0, 3);
  const sources = (
    <>
      {shown.map((s, i) => (
        <Quote key={i} meta={[s.memberName ?? '系统', fmtDateWeek(s.date), s.label].join(' · ')}>
          {s.text}
        </Quote>
      ))}
      {it.edited && <span className="t-meta">这句已手动修改，来源仅供参考</span>}
      {it.projectId && (
        <button type="button" className="wk-src-link" onClick={() => navigate(`/projects/${it.projectId}`)}>
          {it.sources.length > shown.length ? `另有 ${it.sources.length - shown.length} 条 · ` : ''}在项目动态中查看 ›
        </button>
      )}
    </>
  );

  return (
    <div ref={rowRef} className={clsx('wk-item', editing && 'editing')} onMouseEnter={mobile ? undefined : place}>
      <span className="ic">
        <StatusIcon status={icon} />
      </span>
      {editing ? (
        <div className="flex1">
          <textarea
            ref={ref}
            className="wk-edit"
            value={text}
            rows={1}
            onChange={(e) => setText(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => {
              if (e.nativeEvent.isComposing) return;
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                save();
              }
              if (e.key === 'Escape') {
                e.stopPropagation();
                setText(it.text);
                setEditing(null);
              }
            }}
          />
          <div className="wk-edit-bar">
            <span className="t-meta">↵ 保存 · Esc 取消</span>
            <span className="flex1" />
            <button
              type="button"
              className="wk-del"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setEditing(null);
                remove.mutate({ id: w.id!, itemId: it.id });
              }}
            >
              删除这条
            </button>
          </div>
        </div>
      ) : (
        <span
          className={clsx('text', w.canEdit && 'editable')}
          onClick={() => w.canEdit && setEditing(it.id)}
          title={w.canEdit ? '点击修改' : undefined}
        >
          {it.text}
        </span>
      )}
      {!editing && (
        <button type="button" className="wk-count" onClick={() => setOpen(!open)} aria-label={`${it.sources.length} 条来源`} aria-expanded={open}>
          <Icon name="doc" size={12} stroke={1.4} />
          {it.sources.length}
        </button>
      )}
      {!editing && it.sources.length > 0 && (mobile ? open && <div className="wk-src-inline">{sources}</div> : <div className={clsx('wk-src', open && 'open', up && 'up')}>{sources}</div>)}
    </div>
  );
}

/** 一段：标题右侧悬停出现「添加」，不占额外高度 */
function Section({ w, label, section, children }: { w: WeeklyDTO; label: string; section: WeeklySection; children: ReactNode }) {
  const [adding, setAdding] = useState(false);
  return (
    <section className="wk-sec">
      <div className="wk-sec-head">
        <span>{label}</span>
        {w.canEdit && !adding && (
          <button type="button" className="wk-add" onClick={() => setAdding(true)}>
            <Icon name="plus" size={12} />
            添加
          </button>
        )}
      </div>
      {children}
      {adding && <AddItem w={w} section={section} onDone={() => setAdding(false)} />}
    </section>
  );
}

function AddItem({ w, section, onDone }: { w: WeeklyDTO; section: WeeklySection; onDone: () => void }) {
  const { add } = useWeeklyActions();
  const [text, setText] = useState('');
  const submit = () => {
    const t = text.trim();
    if (t) add.mutate({ id: w.id!, section, text: t });
    onDone();
  };
  return (
    <div className="wk-item editing">
      <span className="ic">
        <Icon name="plus" size={14} />
      </span>
      <div className="flex1">
        <textarea
          className="wk-edit"
          autoFocus
          rows={1}
          value={text}
          placeholder="例如：PRJ-12 iCharger 量产：10月8日首批发往德国。"
          onChange={(e) => setText(e.target.value)}
          onBlur={submit}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing) return;
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
            if (e.key === 'Escape') {
              e.stopPropagation();
              onDone();
            }
          }}
        />
        <div className="wk-edit-bar">
          <span className="t-meta">手动添加的句子没有来源 · ↵ 保存 · Esc 取消</span>
        </div>
      </div>
    </div>
  );
}
